import { useState, useEffect, useMemo } from 'react';
import { BrowserRouter, Routes, Route, useNavigate, useParams, useLocation } from 'react-router-dom';
import { supabase } from './supabaseClient';
import { ThemeProvider } from './contexts/ThemeContext';
import { AuthProvider } from './contexts/AuthContext';
import { PartyColorProvider } from './contexts/PartyColorContext';
import MeetingItem from './components/MeetingItem';
import AgendaItem from './components/AgendaItem';
import SpeechItem from './components/SpeechItem';
import LoadingSpinner from './components/LoadingSpinner';
import ErrorMessage from './components/ErrorMessage';
import { SelectMeetingEmptyState, SelectAgendaItemEmptyState, NoSpeechesEmptyState } from './components/EmptyState';
import Header from './components/Header';
import Footer from './components/Footer';
import Panel from './components/Panel';
import SpeechSummaryPopup from './components/SpeechSummaryPopup';
import Profile from './components/Profile';
import Dashboard from './components/Dashboard';
import FilterControls from './components/FilterControls';
import FilterSidebar from './components/FilterSidebar';
import SpeechesPage from './components/SpeechesPage';
import ParliamentMembersPage from './components/ParliamentMembersPage';
import DebugInfo from './components/DebugInfo';
import { generateSpeechSummary } from './claudeApi';
import PasswordProtection from './components/PasswordProtection';
import { 
  extractUniqueHashtags, 
  extractUniqueSpeakers, 
  applyAllFilters, 
  getResultCounts 
} from './utils/filterUtils';

const MainView = () => {
  const navigate = useNavigate();
  const { meetingId } = useParams();
  const location = useLocation();
  // Original data states
  const [allMeetings, setAllMeetings] = useState([]);
  const [allAgendaItems, setAllAgendaItems] = useState([]);
  const [allSpeeches, setAllSpeeches] = useState([]);
  
  // UI states
  const [selectedMeeting, setSelectedMeeting] = useState(null);
  const [selectedAgendaItem, setSelectedAgendaItem] = useState(null);
  const [speeches, setSpeeches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [speechesLoading, setSpeechesLoading] = useState(false);
  const [error, setError] = useState(null);
  const [summaryPopupOpen, setSummaryPopupOpen] = useState(false);
  const [summaryLoading, setSummaryLoading] = useState(false);
  const [summary, setSummary] = useState('');
  const [summaryAgendaItem, setSummaryAgendaItem] = useState(null);
  const [isFilterOpen, setIsFilterOpen] = useState(false);

  // Filter states
  const [filters, setFilters] = useState({
    dateRange: { start: '', end: '' },
    selectedHashtags: [],
    selectedSpeakers: []
  });

  // Initialize filters from navigation state
  useEffect(() => {
    if (location.state && location.state.filters) {
      setFilters(location.state.filters);
    }
  }, [location.state]);

  // Set selected meeting when accessing mobile details route
  useEffect(() => {
    if (meetingId && allMeetings.length > 0) {
      const found = allMeetings.find(m => String(m.id) === String(meetingId));
      setSelectedMeeting(found || null);
    }
  }, [meetingId, allMeetings]);

  // Fetch all data on component mount
  useEffect(() => {
    async function fetchAllData() {
      try {
        setLoading(true);
        
        // Fetch all meetings
        const { data: meetingsData, error: meetingsError } = await supabase
          .from('meetings')
          .select('*')
          .order('date_of_sitting', { ascending: false });

        if (meetingsError) throw meetingsError;

        // Fetch all agenda items
        const { data: agendaItemsData, error: agendaItemsError } = await supabase
          .from('agenda_items')
          .select('*')
          .order('item_number', { ascending: true });

        if (agendaItemsError) throw agendaItemsError;

        // Fetch all speeches
        const { data: speechesData, error: speechesError } = await supabase
          .from('speeches')
          .select('*')
          .order('time', { ascending: true });

        if (speechesError) throw speechesError;

        setAllMeetings(meetingsData || []);
        setAllAgendaItems(agendaItemsData || []);
        setAllSpeeches(speechesData || []);
      } catch (error) {
        console.error('Error fetching data:', error);
        setError('Failed to fetch data');
      } finally {
        setLoading(false);
      }
    }

    fetchAllData();
  }, []);

  // Apply filters and get filtered data
  const filteredData = useMemo(() => {
    return applyAllFilters(allMeetings, allAgendaItems, allSpeeches, filters);
  }, [allMeetings, allAgendaItems, allSpeeches, filters]);

  // Get filter options
  const hashtagOptions = useMemo(() => {
    return extractUniqueHashtags(allMeetings, allAgendaItems);
  }, [allMeetings, allAgendaItems]);

  const speakerOptions = useMemo(() => {
    return extractUniqueSpeakers(allSpeeches);
  }, [allSpeeches]);

  // Get result counts
  const resultCounts = useMemo(() => {
    return getResultCounts(filteredData);
  }, [filteredData]);

  // Get agenda items for selected meeting
  const agendaItems = useMemo(() => {
    if (!selectedMeeting) return [];
    return filteredData.agendaItems.filter(item => item.meeting_id === selectedMeeting.id);
  }, [selectedMeeting, filteredData.agendaItems]);

  // Fetch speeches when an agenda item is selected - with separate loading state
  useEffect(() => {
    if (!selectedAgendaItem) {
      setSpeeches([]);
      return;
    }

    async function fetchSpeeches() {
      try {
        setSpeechesLoading(true);
        const { data, error } = await supabase
          .from('speeches')
          .select('*')
          .eq('agenda_item_id', selectedAgendaItem.id)
          .order('time', { ascending: true });

        if (error) throw error;
        setSpeeches(data || []);
      } catch (error) {
        console.error('Error fetching speeches:', error);
        setError('Failed to fetch speeches');
      } finally {
        setSpeechesLoading(false);
      }
    }

    fetchSpeeches();
  }, [selectedAgendaItem]);

  // Reset selections when filters change
  useEffect(() => {
    // Check if selected meeting is still in filtered results
    if (selectedMeeting && !filteredData.meetings.find(m => m.id === selectedMeeting.id)) {
      setSelectedMeeting(null);
      setSelectedAgendaItem(null);
    }
    
    // Check if selected agenda item is still in filtered results
    if (selectedAgendaItem && !filteredData.agendaItems.find(item => item.id === selectedAgendaItem.id)) {
      setSelectedAgendaItem(null);
    }
  }, [filteredData, selectedMeeting, selectedAgendaItem]);

  // Store speeches for the current agenda item
  const [currentSpeeches, setCurrentSpeeches] = useState([]);

  // Handle summary button click
  const handleSummaryClick = async (agendaItem) => {
    console.log('handleSummaryClick called with:', agendaItem);
    setSummaryAgendaItem(agendaItem);
    setSummaryPopupOpen(true);
    setSummary('');
    console.log('Summary popup should be open now');
    
    try {
      // Fetch speeches for the agenda item if not already selected
      let speechesToSummarize = speeches;
      
      if (!selectedAgendaItem || selectedAgendaItem.id !== agendaItem.id) {
        const { data, error } = await supabase
          .from('speeches')
          .select('*')
          .eq('agenda_item_id', agendaItem.id)
          .order('time', { ascending: true });
          
        if (error) throw error;
        speechesToSummarize = data || [];
      }
      
      // Store the speeches for later use
      setCurrentSpeeches(speechesToSummarize);
    } catch (err) {
      console.error('Error fetching speeches:', err);
      setSummary(`Error fetching speeches: ${err.message}`);
    }
  };

  // Generate summary with custom prompt
  const handleGenerateSummary = async (customPrompt) => {
    setSummaryLoading(true);
    
    try {
      // Generate summary using Claude API with custom prompt
      const summaryText = await generateSpeechSummary(currentSpeeches, customPrompt);
      setSummary(summaryText);
    } catch (err) {
      console.error('Error generating summary:', err);
      setSummary(`Error generating summary: ${err.message}`);
    } finally {
      setSummaryLoading(false);
    }
  };

  // Handle filter changes
  const handleFiltersChange = (newFilters) => {
    setFilters(newFilters);
  };

  // Handle clear filters
  const handleClearFilters = () => {
    setFilters({
      dateRange: { start: '', end: '' },
      selectedHashtags: [],
      selectedSpeakers: []
    });
  };

  // Handle hashtag click - add to filter
  const handleHashtagClick = (hashtag) => {
    const newSelectedHashtags = filters.selectedHashtags.includes(hashtag)
      ? filters.selectedHashtags.filter(h => h !== hashtag) // Remove if already selected
      : [...filters.selectedHashtags, hashtag]; // Add if not selected

    setFilters({
      ...filters,
      selectedHashtags: newSelectedHashtags
    });
  };

  const handleMeetingClick = (meeting) => {
    if (window.innerWidth < 768) {
      navigate(`/meetings/mobile/${meeting.id}`, { state: { filters } });
    } else {
      setSelectedMeeting(meeting);
    }
  };

  const handleMobileBack = () => {
    navigate('/meetings', { state: { filters } });
  };

  console.log('MainView render - summaryPopupOpen:', summaryPopupOpen);

  return (
    <div className="min-h-screen bg-gray-100 dark:bg-gray-900 flex flex-col transition-colors duration-200">
      <Header />

      <div className="flex flex-1 relative">
        {/* Mobile filter toggle */}
        <button
          onClick={() => setIsFilterOpen(true)}
          className="md:hidden fixed top-40 left-2 z-30 inline-flex items-center px-3 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none"

        >
          <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 6h16M4 12h16M4 18h16" />
          </svg>
          Filters
        </button>


        {meetingId && (
          <button
            onClick={handleMobileBack}
            className="md:hidden fixed top-16 left-4 z-30 inline-flex items-center p-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none"
          >
            Back
          </button>
        )}

        {/* Filter Sidebar */}
        <FilterSidebar
          filters={filters}
          onFiltersChange={handleFiltersChange}
          hashtagOptions={hashtagOptions}
          speakerOptions={speakerOptions}
          onClearFilters={handleClearFilters}
          resultCounts={resultCounts}
          isMobileOpen={isFilterOpen}
          onClose={() => setIsFilterOpen(false)}
          className="md:w-64 md:mr-4"
        />

        {/* Overlay when mobile filters open */}
        {isFilterOpen && (
          <div
            className="md:hidden fixed inset-0 bg-black bg-opacity-50 z-30"
            onClick={() => setIsFilterOpen(false)}
          />
        )}

        {/* Main Content */}
        <main className="flex-1 p-4 md:pl-0">
          <ErrorMessage message={error} />

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Meetings Panel */}
            {!(window.innerWidth < 768 && meetingId) && (
              <Panel title="Meetings">
                {loading ? (
                  <LoadingSpinner message="Loading meetings..." />
                ) : (
                  <div className="divide-y divide-gray-200 dark:divide-gray-700">
                    {filteredData.meetings.map((meeting) => (
                      <MeetingItem
                        key={meeting.id}
                        meeting={meeting}
                        isSelected={selectedMeeting?.id === meeting.id}
                        onClick={() => handleMeetingClick(meeting)}
                        onHashtagClick={handleHashtagClick}
                        agendaItems={filteredData.agendaItems}
                      />
                    ))}
                  </div>
                )}
              </Panel>
            )}

            {/* Agenda Items Panel */}
            <Panel title="Agenda Items">
              {!selectedMeeting ? (
                <SelectMeetingEmptyState />
              ) : loading ? (
                <LoadingSpinner message="Loading agenda items..." />
              ) : agendaItems.length === 0 ? (
                <div className="p-4 text-center text-gray-500 dark:text-gray-400">
                  No agenda items found for the selected filters.
                </div>
              ) : (
                <div className="divide-y divide-gray-200 dark:divide-gray-700">
                  {agendaItems.map((item) => (
                    <AgendaItem 
                      key={item.id}
                      item={item}
                      isSelected={selectedAgendaItem?.id === item.id}
                      onClick={() => setSelectedAgendaItem(item)}
                      onSummaryClick={handleSummaryClick}
                      onHashtagClick={handleHashtagClick}
                    />
                  ))}
                </div>
              )}
            </Panel>
          </div>
        </main>
      </div>

      <Footer />
      
      <SpeechSummaryPopup 
        isOpen={summaryPopupOpen}
        onClose={() => setSummaryPopupOpen(false)}
        summary={summary}
        loading={summaryLoading}
        onGenerateSummary={handleGenerateSummary}
      />
    </div>
  );
};

function App() {
  return (
    <ThemeProvider>
      <AuthProvider>
        <PartyColorProvider>
          <BrowserRouter>
            <DebugInfo />
            <Routes>
              <Route path="/" element={<><Header /><Dashboard /><Footer /></>} />
            <Route path="/meetings" element={<MainView />} />
            <Route path="/meetings/mobile/:meetingId" element={<MainView />} />
            <Route path="/parliament-members" element={<ParliamentMembersPage />} />
            <Route path="/speeches/:agendaItemId" element={<SpeechesPage />} />
            <Route path="/profile/:speakerName" element={<Profile />} />
            <Route path="/profile/:speakerName/mobile/:meetingId" element={<Profile />} />
          </Routes>
        </BrowserRouter>
        </PartyColorProvider>
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;
